import type { PaymentMethodType } from '@seek/payments-widget';

import type { AdBudgetType } from '../../../generated/graphql-types';
import type { AdvertiserContract } from '../../../types/advertiser';

// TODO: need a central file for these. This is also in AdBudgets page folder
export interface AdBudgetInfo {
  id: string;
  price: number;
  displayItemTitle: string;
  discountDurationMonths: number;
  percentageSavings: number;
  ads: AdBudgetType;
  currency: string;
  name: string;
}

type AdvertiserContracts = AdvertiserContract[];

export interface CheckoutState {
  orderId?: string;
  orderPlaced: boolean;
  productSetId?: string;
  adBudgetInfo?: AdBudgetInfo;
  advertiserContracts?: AdvertiserContracts;
  adBudgetList?: AdBudgetInfo[];
  idempotencyKey?: string;
  isSaveCreditCardChecked?: boolean;
  isPaidByStoredCreditCard?: boolean;
  paymentMethodSelected: PaymentMethodType;
  purchasePaymentReferenceNumber?: string;
}

export const defaultState: CheckoutState = {
  orderPlaced: false,
  paymentMethodSelected: 'CreditCard',
};

export enum CheckoutActionType {
  SET_CREATED_ORDER = 'SET_CREATED_ORDER',
  SET_CONFIRMED_INVOICE_ORDER = 'SET_CONFIRMED_INVOICE_ORDER',
  SET_FETCHED_BUDGET = 'SET_FETCHED_BUDGET',
  SET_FETCHED_BUDGETS = 'SET_FETCHED_BUDGETS',
  SET_ADVERTISER_CONTRACTS = 'SET_ADVERTISER_CONTRACTS',
  SET_IS_SAVE_CREDIT_CARD_CHECKED = 'SET_IS_SAVE_CREDIT_CARD_CHECKED',
  SET_IS_PAID_BY_STORED_CREDIT_CARD = 'SET_IS_PAID_BY_STORED_CREDIT_CARD',
  SET_PAYMENT_METHOD = 'SET_PAYMENT_METHOD',
}

export interface SetCreatedOrderAction {
  type: CheckoutActionType.SET_CREATED_ORDER;
  orderId: string;
  purchasePaymentReferenceNumber: string;
  productSetId?: string;
  idempotencyKey: string;
}

export interface SetConfirmedInvoiceOrderAction {
  type: CheckoutActionType.SET_CONFIRMED_INVOICE_ORDER;
  orderId: string;
}

export interface SetFetchedBudgetAction {
  type: CheckoutActionType.SET_FETCHED_BUDGET;
  adBudget: AdBudgetInfo;
}
export interface SetFetchedBudgetsAction {
  type: CheckoutActionType.SET_FETCHED_BUDGETS;
  adBudgetList: AdBudgetInfo[];
}

export interface SetAdvertiserContractsAction {
  type: CheckoutActionType.SET_ADVERTISER_CONTRACTS;
  advertiserContracts: AdvertiserContracts;
}

export interface SetIsSaveCreditCardCheckedAction {
  type: CheckoutActionType.SET_IS_SAVE_CREDIT_CARD_CHECKED;
  isSaveCreditCardChecked: boolean;
}

export interface SetIsPaidByStoredCreditCardAction {
  type: CheckoutActionType.SET_IS_PAID_BY_STORED_CREDIT_CARD;
  isPaidByStoredCreditCard: boolean;
}

export interface SetPaymentMethodAction {
  type: CheckoutActionType.SET_PAYMENT_METHOD;
  paymentMethodSelected: PaymentMethodType;
}

export type CheckoutAction =
  | SetCreatedOrderAction
  | SetConfirmedInvoiceOrderAction
  | SetFetchedBudgetAction
  | SetFetchedBudgetsAction
  | SetAdvertiserContractsAction
  | SetIsSaveCreditCardCheckedAction
  | SetIsPaidByStoredCreditCardAction
  | SetPaymentMethodAction;

export const checkoutReducer: React.Reducer<CheckoutState, CheckoutAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case CheckoutActionType.SET_CREATED_ORDER:
      return {
        ...state,
        orderId: action.orderId,
        purchasePaymentReferenceNumber: action.purchasePaymentReferenceNumber,
        orderPlaced: false,
        productSetId: action.productSetId,
        idempotencyKey: action.idempotencyKey,
      };
    case CheckoutActionType.SET_FETCHED_BUDGET:
      return {
        ...state,
        adBudgetInfo: { ...action.adBudget },
      };
    case CheckoutActionType.SET_FETCHED_BUDGETS:
      return {
        ...state,
        adBudgetList: action.adBudgetList,
      };
    case CheckoutActionType.SET_CONFIRMED_INVOICE_ORDER:
      return {
        ...state,
        orderId: action.orderId,
        orderPlaced: true,
      };
    case CheckoutActionType.SET_ADVERTISER_CONTRACTS:
      return {
        ...state,
        advertiserContracts: action.advertiserContracts,
      };
    case CheckoutActionType.SET_IS_SAVE_CREDIT_CARD_CHECKED:
      return {
        ...state,
        isSaveCreditCardChecked: action.isSaveCreditCardChecked,
      };
    case CheckoutActionType.SET_IS_PAID_BY_STORED_CREDIT_CARD:
      return {
        ...state,
        isPaidByStoredCreditCard: action.isPaidByStoredCreditCard,
      };
    case CheckoutActionType.SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethodSelected: action.paymentMethodSelected,
      };
    default:
      return state;
  }
};
